
import { useFormState } from "./useFormState";
import { useFormFields } from "./useFormFields";
import { useFormSave } from "./useFormSave";
import { useFormData } from "./useFormData";
import { useFormCreation } from "./useFormCreation";
import { Shop, Form } from "@/components/dashboard/shops/types";
import { useEffect } from "react";

export const useFormSettings = (shop: Shop) => {
  // Get form state (editing, saving states, etc.)
  const formState = useFormState(shop);
  
  // Load form data
  const formData = useFormData(shop.id);
  
  // Handle form fields management
  const formFields = useFormFields(formData.currentForm);
  
  // Handle form creation
  const formCreation = useFormCreation(shop, formData.refreshForms, formData.setCurrentForm, formFields.createFormTemplate, formFields.setFormFields);
  
  // Handle form saving
  const { handleSave } = useFormSave(
    formData.currentForm, 
    {
      setIsSaving: formState.setIsSaving,
      setIsEditing: formState.setIsEditing,
      formName: formState.formName,
      ctaButtonText: formState.ctaButtonText,
      formProductIds: formState.formProductIds,
      phone: formState.phone,
      customerName: formState.customerName,
      email: formState.email,
      country: formState.country,
      region: formState.region,
      municipality: formState.municipality,
      address: formState.address,
      externalId: formState.externalId
    }, 
    formFields.formFields
  );

  // Determine if a form template exists
  const hasFormTemplate = (formData.currentForm?.fields && formData.currentForm.fields.length > 0) || 
                          (formFields.formFields && formFields.formFields.length > 0);
  
  // Create a new form template locally without saving to database
  const createNewFormTemplate = () => {
    const defaultFields = formFields.createFormTemplate();
    formFields.setFormFields(defaultFields);
    formState.setFormName("Nouveau formulaire");
    formState.setCtaButtonText("Acheter maintenant");
    formState.setFormProductIds([]);
    formData.setCurrentForm(null); // Clear the current form since we're creating a new one
    
    // Initialize other fields with empty values
    formState.setPhone("");
    formState.setCustomerName("");
    formState.setEmail("");
    formState.setCountry("Algeria");
    formState.setRegion("");
    formState.setMunicipality("");
    formState.setAddress("");
    formState.setExternalId(`FORM-${Date.now()}`);
  };

  // Update local form states when current form changes
  useEffect(() => {
    if (formData.currentForm) {
      formState.setFormName(formData.currentForm.name);
      formState.setCtaButtonText(formData.currentForm.cta_button_text);
      formState.setFormProductIds(formData.currentForm.product_ids || []);
      formFields.setFormFields(formData.currentForm.fields);
      
      // Set customer info fields
      formState.setPhone(formData.currentForm.phone || "");
      formState.setCustomerName(formData.currentForm.customer_name || "");
      formState.setEmail(formData.currentForm.email || "");
      formState.setCountry(formData.currentForm.country || "Algeria");
      formState.setRegion(formData.currentForm.region || "");
      formState.setMunicipality(formData.currentForm.municipality || "");
      formState.setAddress(formData.currentForm.address || "");
      formState.setExternalId(formData.currentForm.external_id || "");
    }
  }, [formData.currentForm]);

  return {
    // Form state
    isEditing: formState.isEditing,
    setIsEditing: formState.setIsEditing,
    isSaving: formState.isSaving,
    formName: formState.formName,
    setFormName: formState.setFormName,
    ctaButtonText: formState.ctaButtonText,
    setCtaButtonText: formState.setCtaButtonText,
    
    // Form fields
    formFields: formFields.formFields,
    toggleFieldEnabled: formFields.toggleFieldEnabled,
    toggleFieldRequired: formFields.toggleFieldRequired,
    deleteField: formFields.deleteField,
    addNewField: formFields.addNewField,
    updateFieldName: formFields.updateFieldName,
    updateFieldType: formFields.updateFieldType,
    
    // Form data & operations
    handleSave,
    hasFormTemplate,
    handleCreateForm: formCreation.handleCreateForm,
    forms: formData.forms,
    currentForm: formData.currentForm,
    setCurrentForm: formData.setCurrentForm,
    formProductIds: formState.formProductIds,
    setFormProductIds: formState.setFormProductIds,
    isLoading: formData.isLoading,
    refreshForms: formData.refreshForms,
    
    // Customer info fields
    phone: formState.phone,
    setPhone: formState.setPhone,
    customerName: formState.customerName,
    setCustomerName: formState.setCustomerName,
    email: formState.email,
    setEmail: formState.setEmail,
    country: formState.country,
    setCountry: formState.setCountry,
    region: formState.region,
    setRegion: formState.setRegion,
    municipality: formState.municipality,
    setMunicipality: formState.setMunicipality,
    address: formState.address,
    setAddress: formState.setAddress,
    externalId: formState.externalId,
    setExternalId: formState.setExternalId,
    
    // New form template creation (local only)
    createNewFormTemplate
  };
};
